import type { Qualitys } from '@module/types/play/player';
import TimeConstants from '@module/utils/item';
import type { MediaPlayer } from 'amazon-ivs-player';
import type Player from 'video.js/dist/types/player';
import { create, type StateCreator } from 'zustand';
import {
  ivsQualitySafeLocalStorage,
  plyrStateSafeLocalStorage,
  streamModeSafeLocalStorage,
} from '../lib/storage/storageImplementations';

const isMediaPlayer = (player: unknown): player is MediaPlayer => {
  return !!player && typeof (player as MediaPlayer).setQuality === 'function';
};
const isVideoJsPlayer = (player: unknown): player is Player => {
  return !!player && typeof (player as Player).dispose === 'function';
};

// ivsPlayer(IVS 저수준 엔진)
export const getIVSMediaPlayer = (ivsPlayer: MediaPlayer | null): MediaPlayer | null =>
  ivsPlayer && isMediaPlayer(ivsPlayer) ? ivsPlayer : null;
//  video.js 인스턴스
export const getVideoJsPlayer = (player: Player | HTMLVideoElement | null): Player | null =>
  player && !(player instanceof HTMLVideoElement) ? player : null;
// RT 비디오 엘리먼트
export const getRTVideoElement = (player: Player | HTMLVideoElement | null): HTMLVideoElement | null =>
  player instanceof HTMLVideoElement ? player : null;

// 저장된 재생 상태를 기반으로 초기값 계산 (usePlayerStore와 동일 패턴)
const storedPlyrState = plyrStateSafeLocalStorage.get();
const INITIAL_VOLUME = storedPlyrState?.volume ?? 0;
const INITIAL_MUTED = (storedPlyrState?.volume === 0 || storedPlyrState?.muted) ?? true;
// 클라이언트 LL/RT 재생 모드. 디폴트 RT, localStorage 영속.
const INITIAL_STREAM_MODE = streamModeSafeLocalStorage.get() ?? 'RT';

interface PlayerState {
  // 공통 재생 핸들.
  // LL: video.js Player 인스턴스 / RT: HTMLVideoElement
  player: Player | HTMLVideoElement | null;
  setPlayer: (player: Player | HTMLVideoElement | null) => void;
  // LL 전용 IVS 저수준 엔진 핸들(MediaPlayer). RT에서는 없으므로 null.
  ivsPlayer: MediaPlayer | null;
  setIvsPlayer: (ivsPlayer: MediaPlayer | null) => void;
  isPlayerFullscreen: boolean;
  volume: number;
  setVolume: (volume: number) => void;
  volumeBefore: number;
  setVolumeBefore: (volume: number) => void;
  isMute: boolean;
  setIsMute: (isMute: boolean) => void;
  togglePlayerFullscreen: () => void;
  setPlayerFullscreen: (val: boolean) => void;
  isPipMode: boolean;
  setIsPipMode: (val: boolean | ((prev: boolean) => boolean)) => void;
  isPlayerToolbarVisible: boolean;
  setIsPlayerToolbarVisible: (val: boolean | ((prev: boolean) => boolean)) => void;
  idle: boolean;
  setIdle: (idle: boolean) => void;
  isPlay: boolean;
  setIsPlay: (isPlay: boolean | ((prev: boolean) => boolean)) => void;
  // 첫 진입 덮힘 화면 플래그
  isBeforeFirstPlay: boolean;
  setIsBeforeFirstPlay: (val: boolean) => void;
  // 화질 패널·마우스 진입 UI 상태.
  isOpenQualityPane: boolean;
  setIsOpenQualityPane: (val: boolean | ((prev: boolean) => boolean)) => void;
  // 시청방식(RT/LL) 패널 UI 상태.
  isOpenStreamModePane: boolean;
  setIsOpenStreamModePane: (val: boolean | ((prev: boolean) => boolean)) => void;
  isEnterMouseInVideo: boolean;
  setIsEnterMouseInVideo: (val: boolean | ((prev: boolean) => boolean)) => void;
  isEnterMouseInToolbar: boolean;
  setIsEnterMouseInToolbar: (val: boolean | ((prev: boolean) => boolean)) => void;
  // 화질 상태.
  qualities: Qualitys[] | undefined;
  setQualities: (val: Qualitys[] | undefined) => void;
  currentQuality: Qualitys | undefined;
  setCurrentQuality: (val: Qualitys | undefined) => void;
  // 자동 화질 모드 여부.
  isAutoQuality: boolean;
  setIsAutoQuality: (v: boolean) => void;
  // 클라이언트 LL/RT 재생 모드 선택값. (playInfo.playMode 와 무관) 디폴트 RT, localStorage 영속.
  // setStreamMode 호출 → usePandaPlayer 에서 isRT 가 뒤집혀 LL/RT 플레이어가 in-place 로 교체됨.
  streamMode: 'RT' | 'LL';
  setStreamMode: (mode: 'RT' | 'LL') => void;
  // RT 모드 등록 핸들러
  rtQualityHandlers: {
    selectQuality: (quality: Qualitys) => void;
    selectAutoQuality: () => void;
  } | null;
  setRTQualityHandlers: (handlers: PlayerState['rtQualityHandlers']) => void;
  selectQuality: (quality: Qualitys) => void;
  selectAutoQuality: () => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  rtSessionCleanup: (() => void) | null;
  setRTSessionCleanup: (cleanup: (() => void) | null) => void;
  dispose: () => void;
}

const store: StateCreator<PlayerState, [], [], PlayerState> = (set, get) => {
  let toolbarTimeout: NodeJS.Timeout | null = null;
  return {
    player: null,
    setPlayer: (player: Player | HTMLVideoElement | null) => {
      const { volume, isMute } = get();

      if (isVideoJsPlayer(player)) {
        player.volume(volume);
        player.muted(isMute);
      } else if (player instanceof HTMLVideoElement) {
        player.volume = volume;
        player.muted = isMute;
      }
      set({ player });
    },
    ivsPlayer: null,
    setIvsPlayer: (ivsPlayer: MediaPlayer | null) => set({ ivsPlayer }),
    isPlayerFullscreen: false,
    volume: INITIAL_VOLUME,
    // store.setVolume → 실제 player(LL: MediaPlayer, RT: HTMLVideoElement)도 함께 제어.
    setVolume: (volume: number) => {
      const { ivsPlayer, player } = get();
      const ivs = getIVSMediaPlayer(ivsPlayer);
      const rtVideo = getRTVideoElement(player);
      if (ivs) {
        ivs.setVolume(volume);
        ivs.setMuted(volume === 0);
      } else if (rtVideo) {
        rtVideo.volume = volume;
        rtVideo.muted = volume === 0;
      }
      set({ volume, isMute: volume === 0 });
    },
    volumeBefore: INITIAL_VOLUME,
    setVolumeBefore: (volume: number) => set({ volumeBefore: volume }),
    isMute: INITIAL_MUTED,
    // store.setIsMute → 실제 player도 함께 mute/unmute 제어.
    setIsMute: (isMute: boolean) => {
      const { ivsPlayer, player } = get();
      const ivs = getIVSMediaPlayer(ivsPlayer);
      const rtVideo = getRTVideoElement(player);
      const storedVolume = plyrStateSafeLocalStorage.get()?.volume || 0.5;
      const nextVolume = storedVolume;
      if (ivs) {
        ivs.setMuted(isMute);
        ivs.setVolume(nextVolume);
      } else if (rtVideo) {
        rtVideo.muted = isMute;
        rtVideo.volume = nextVolume;
      }
      plyrStateSafeLocalStorage.set({
        muted: isMute,
        volume: nextVolume,
      });
      set({ isMute, volume: nextVolume });
    },
    togglePlayerFullscreen: () => set((state) => ({ isPlayerFullscreen: !state.isPlayerFullscreen })),
    setPlayerFullscreen: (val) => set({ isPlayerFullscreen: val }),
    isPipMode: false,
    setIsPipMode: (val) =>
      set((state) => ({
        isPipMode: typeof val === 'function' ? val(state.isPipMode) : val,
      })),
    isPlayerToolbarVisible: false,
    setIsPlayerToolbarVisible: (val) => {
      if (toolbarTimeout) {
        clearTimeout(toolbarTimeout);
        toolbarTimeout = null;
      }
      const next = typeof val === 'function' ? val(get().isPlayerToolbarVisible ?? false) : val;

      set({ isPlayerToolbarVisible: next });

      if (next) {
        toolbarTimeout = setTimeout(() => {
          set({ isPlayerToolbarVisible: false });
        }, TimeConstants.ONE_SECOND * 3);
      }
    },
    idle: false,
    setIdle: (idle: boolean) => set({ idle }),
    isPlay: false,
    setIsPlay: (isPlay: boolean | ((prev: boolean) => boolean)) =>
      set((state) => ({
        isPlay: typeof isPlay === 'function' ? isPlay(state.isPlay) : isPlay,
      })),
    isBeforeFirstPlay: true,
    setIsBeforeFirstPlay: (val) => set({ isBeforeFirstPlay: val }),
    // 화질 패널·마우스 진입 UI 상태.
    isOpenQualityPane: false,
    setIsOpenQualityPane: (val) =>
      set((state) => ({
        isOpenQualityPane: typeof val === 'function' ? val(state.isOpenQualityPane) : val,
      })),
    isOpenStreamModePane: false,
    setIsOpenStreamModePane: (val) =>
      set((state) => ({
        isOpenStreamModePane: typeof val === 'function' ? val(state.isOpenStreamModePane) : val,
      })),
    isEnterMouseInVideo: false,
    setIsEnterMouseInVideo: (val) =>
      set((state) => ({
        isEnterMouseInVideo: typeof val === 'function' ? val(state.isEnterMouseInVideo) : val,
      })),
    isEnterMouseInToolbar: false,
    setIsEnterMouseInToolbar: (val) =>
      set((state) => ({
        isEnterMouseInToolbar: typeof val === 'function' ? val(state.isEnterMouseInToolbar) : val,
      })),
    // 화질 상태.
    qualities: undefined,
    setQualities: (val) => {
      if (!val) {
        set({ qualities: val });
        return;
      }

      const getResolution = (name: string): number => {
        const match = name.match(/(\d+)p/);
        return match ? parseInt(match[1], 10) : -1;
      };

      const allParseable = val.length > 0 && val.every((q) => getResolution(q.name) !== -1);
      // 간헐적으로 압축 효율 때문에 실제 1080p이 고화질임에도 비트레이트가 낮게 와 순서가 뒤로 밀리는 경우가 존재.
      // 서버 name이 변경되어 파싱이 정상적이 안된다면 기본값을 그대로 전달
      const sortedQualities = allParseable
        ? [...val].sort((a, b) => getResolution(b.name) - getResolution(a.name))
        : val;

      set({ qualities: sortedQualities });
    },
    currentQuality: undefined,
    setCurrentQuality: (val) => set({ currentQuality: val }),
    // RT 전용 자동 화질 플래그. LL은 ivsPlaye 자체 isAutoQualityMode() 사용
    isAutoQuality: true,
    setIsAutoQuality: (v) => set({ isAutoQuality: v }),
    // 클라이언트 LL/RT 재생 모드. set 시 localStorage 에도 영속. dispose 에서는 리셋하지 않음(선호 유지).
    streamMode: INITIAL_STREAM_MODE,
    setStreamMode: (streamMode) => {
      streamModeSafeLocalStorage.set(streamMode);
      set({ streamMode });
    },
    rtQualityHandlers: null,
    setRTQualityHandlers: (handlers) => set({ rtQualityHandlers: handlers }),
    // LL/RT 공통 재생/정지
    play: () => {
      const { ivsPlayer, player } = get();
      const ivs = getIVSMediaPlayer(ivsPlayer);
      const rtVideo = getRTVideoElement(player);
      if (ivs) {
        ivs.play();
      } else if (rtVideo) {
        rtVideo.play();
        set({ isPlay: true, idle: false });
      }
    },
    pause: () => {
      const { ivsPlayer, player } = get();
      const ivs = getIVSMediaPlayer(ivsPlayer);
      const rtVideo = getRTVideoElement(player);
      if (ivs) {
        ivs.pause();
      } else if (rtVideo) {
        rtVideo.pause();
        set({ isPlay: false, idle: true });
      }
    },
    togglePlay: () => {
      const { isPlay, play, pause } = get();
      isPlay ? pause() : play();
    },
    // 통합 진입점. RT 핸들러 유무·ivsPlayer 타입에 따라 분기.
    selectQuality: (quality) => {
      const { rtQualityHandlers, ivsPlayer } = get();
      if (rtQualityHandlers) {
        rtQualityHandlers.selectQuality(quality);
        return;
      }
      const ivs = getIVSMediaPlayer(ivsPlayer);
      if (ivs) {
        ivs.setQuality(quality);
        ivsQualitySafeLocalStorage.set(quality);
      }
    },
    selectAutoQuality: () => {
      const { rtQualityHandlers, ivsPlayer } = get();
      if (rtQualityHandlers) {
        rtQualityHandlers.selectAutoQuality();
        return;
      }
      const ivs = getIVSMediaPlayer(ivsPlayer);
      if (ivs) {
        ivs.setAutoQualityMode(true);
        ivsQualitySafeLocalStorage.remove();
      }
    },
    rtSessionCleanup: null,
    setRTSessionCleanup: (cleanup) => set({ rtSessionCleanup: cleanup }),
    dispose: () => {
      const { player, ivsPlayer, rtSessionCleanup } = get();

      // LL 모드: ivsPlayer 존재 ⇒ player는 video.js Player. Player 정리 + IVS MediaPlayer 정리.
      const ivs = getIVSMediaPlayer(ivsPlayer);
      const videoJsPlayer = getVideoJsPlayer(player);
      const rtVideo = getRTVideoElement(player);

      videoJsPlayer?.dispose();
      ivs?.delete();
      // RT 모드: HTMLVideoElement의 MediaStream 트랙 정리 + srcObject 해제. 세션 정리는 훅에서 등록한 콜백으로 수행.
      if (rtVideo) {
        rtVideo.pause();
        if (rtVideo.srcObject instanceof MediaStream) {
          rtVideo.srcObject.getTracks().forEach((track) => track.stop());
        }
        rtVideo.srcObject = null;
      }
      rtSessionCleanup?.();
      set({
        player: null,
        ivsPlayer: null,
        rtSessionCleanup: null,
        rtQualityHandlers: null,
        idle: false,
        isPlay: false,
        isBeforeFirstPlay: true,
        isOpenQualityPane: false,
        isOpenStreamModePane: false,
        isEnterMouseInVideo: false,
        isEnterMouseInToolbar: false,
        qualities: undefined,
        currentQuality: undefined,
        isAutoQuality: true,
      });
    },
  };
};

const useOldPlayerStore = create<PlayerState>()(store);

/**
 * LL/RT에 맞는 자동 화질 모드 여부를 반환.
 * - RT: store.isAutoQuality
 * - LL: ivsPlayer.isAutoQualityMode() (SDK 메서드 사용)
 */
export const useIsAutoQuality = () =>
  useOldPlayerStore((s) => {
    if (s.rtQualityHandlers) return s.isAutoQuality;
    const ivs = getIVSMediaPlayer(s.ivsPlayer);
    return ivs ? !!ivs.isAutoQualityMode() : false;
  });

export default useOldPlayerStore;
